// Modules
import React, { useCallback, Dispatch, SetStateAction, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@global/store/hooks';

// Components
import { ShippingForm } from './ShippingForm';
import { useAutocomplete, SuggestAddress } from '@global/hooks/useAutocomplete';
import { Spinner } from '@global/components/UI Elements/Spinner';

// Redux
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import { cartSelector } from '@global/store/slices/cart/selectors';

// Utils
import { useYupValidationResolver } from '@global/components/ActiveForm/utils';
import { CountryService } from '@global/utils/CountryService';
import { getLocaleConfig } from '@global/localization/services';

// Types
import { AddressType } from '@global/types/types';
import { shippingInformationSchema } from './rules';
import { ShippingInfoFormValuesType } from './types';

import './style.scss';
import { Addresses } from '@src/global/components/Addresses';
import { useSavedCustomerAddresses } from '@src/global/hooks/useSavedCustomerAddresses';
import { SHIPPING_US_REGION_RESTRICTED_COUNTRIES } from '../../consts';
import { RestrictedCountryModal } from '@src/global/components/RestrictedCountryModal';
import { useModal } from '@src/global/hooks/useModal';
import { OrderItemType } from '@src/global/utils/orderUtils';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface IProps {
  isLoading: boolean;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  onSubmit: (formData: ShippingInfoFormValuesType) => void;
  onSelectCountry?: (country: string) => void;
  onSave: (formData: ShippingInfoFormValuesType) => void;
}

export const ShippingInformation: React.FC<IProps> = ({
  isLoading,
  onSubmit,
  onSelectCountry,
  onSave,
}) => {
  const { t } = useTranslation();
  const cartState = useAppSelector(cartSelector);
  const countries = useAppSelector(countriesSelector);

  const isEnabledEmailField = cartState.isGuestOrder;
  const resolverSchema = shippingInformationSchema({
    enableEmail: isEnabledEmailField,
  });

  const selectedDeliveryOption =
    cartState.wSelectedDeliveryOption?.wdoCode ||
    cartState.deliveryOptions[0]?.id;

  const {
    savedCustomerAddresses,
    selectedAddress,
    selectedAddressUid,
    isNewAddress,

    editAddress,
    selectAddress,
    createNewAddress,
    checkIfAddressExists,
  } = useSavedCustomerAddresses(AddressType.SHIPPING);

  const rulesResolver = useYupValidationResolver(resolverSchema);

  const methods = useForm<ShippingInfoFormValuesType>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: rulesResolver,
  });

  const selectedCountry = methods.watch('country');

  // Get deliveryOptions by selected country code
  useEffect(() => {
    if (!onSelectCountry) return;
    const { iso } = SiteConfig.getConfig();
    const { isDirty } = methods.getFieldState('country', methods.formState);
    const country = selectedCountry?.toLowerCase();

    if (country === iso && !isDirty) return;
    onSelectCountry(country || iso);
  }, [selectedCountry]);

  // Get deliveryOptions by selected saved address country code
  useEffect(() => {
    if (!onSelectCountry || !selectedAddress) return;
    const country = selectedAddress.wCountry?.toLowerCase();

    if (!country) return;
    onSelectCountry(country);
  }, [selectedAddress]);

  const setAddressFormRegionData = useCallback(
    (data: SuggestAddress) => {
      const countryCode = methods.getValues('country');
      const country = CountryService.getCountryByIsoCode(
        countries,
        countryCode
      );
      if (!country) return;

      const { stateDropdown } = getLocaleConfig(country?.isoCodeTwo);
      const currentState = CountryService.getStateByName(
        country.regions,
        data.state
      );
      const state = stateDropdown ? currentState?.isoCodeShort : '';

      methods.setValue('city', data.city);
      methods.setValue('state', state);
      methods.trigger(['city', 'state']);
    },
    [countries, methods]
  );

  const onChangeSuggestions = useCallback(
    (suggestions: SuggestAddress[]) => {
      const suggestedAddress = suggestions[0];
      if (suggestedAddress) setAddressFormRegionData(suggestedAddress);
    },
    [setAddressFormRegionData]
  );

  const { onChangeZipCode } = useAutocomplete({ onChangeSuggestions });

  useEffect(() => {
    // Autocomplete with zip code
    const subscription = methods.watch((value, { name }) => {
      if (name === 'zip' && selectedAddress?.upaPostcode !== value.zip) {
        onChangeZipCode(value.zip, value.country);
      }
    });

    return () => subscription.unsubscribe();
  }, [selectedAddress]);

  const { handleOpen: openRestrictedCountryModal } = useModal<OrderItemType[]>({
    hideCloseButton: true,
    customClass: 'restricted-modal',
    customOverlayClass: 'restricted-modal-overlay',
    content: ({ handleClose, customData }) => (
      <RestrictedCountryModal
        type="shipping"
        handleClose={handleClose}
        customData={customData}
      />
    ),
  });

  return isLoading ? (
    <div className="shipping-info__spinner">
      <Spinner size={'64px'} />
    </div>
  ) : (
    <div className="shipping-info">
      <p className="shipping-info__subtitle">
        {t('checkout.shippingAddress.subTitle')}
      </p>
      <Addresses
        addresses={savedCustomerAddresses}
        selectedAddressUid={selectedAddressUid}
        selectAddress={selectAddress}
        editAddress={editAddress}
        createNewAddress={createNewAddress}
      />
      <FormProvider {...methods}>
        <ShippingForm
          isNewAddress={isNewAddress}
          selectedAddress={selectedAddress}
          enabledAddressDoctor={isNewAddress}
          checkIfAddressExists={checkIfAddressExists}
          hiddenForm={Boolean(selectedAddressUid)}
          onSubmit={onSubmit}
          shippingInfo={cartState.shippingInfo}
          buttonText={t('checkout.shippingAddress.save')}
          isLoading={cartState.isLoading}
          enableEmail={isEnabledEmailField}
          deliveryOptions={cartState.deliveryOptions}
          selectedDeliveryOption={selectedDeliveryOption}
          restrictedCountries={SHIPPING_US_REGION_RESTRICTED_COUNTRIES}
          onCountryRestrictedProducts={openRestrictedCountryModal}
          onSaveShippingInformation={onSave}
          isPrintOnDemand={cartState.isPrintOnDemand}
        />
      </FormProvider>
    </div>
  );
};
